import { useState, useEffect, useCallback, useRef, useMemo, memo, Fragment } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  AlertTitle,
  IconButton,
  Tooltip,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  InputAdornment,
  Chip,
  Collapse,
  Divider,
  MenuItem,
  Autocomplete,
  Checkbox,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import AppDialog from "../General/Dialog/AppDialog";
import ConfirmDialog from "../General/Dialog/ConfirmDialog";
import PreviewDialog from "../General/Dialog/PreviewDialog";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ShowChartRoundedIcon from "@mui/icons-material/ShowChartRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import PictureAsPdfRoundedIcon from "@mui/icons-material/PictureAsPdfRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CloudDownloadRoundedIcon from "@mui/icons-material/CloudDownloadRounded";
import PlaylistAddRoundedIcon from "@mui/icons-material/PlaylistAddRounded";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { useTheme } from "@mui/material/styles";
import { formatCurrency } from "../../config/Helper";
import { useToast } from "../../context/ToastContext";
import { useAuth } from "../../context/AuthContext";
import CurrencyInput from "../General/CurrencyInput";
import TaxReceiptsDialog from "./TaxReceiptsDialog";
import LiquidationSheetDialog from "./LiquidationSheetDialog";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import BancoCordobaRatesDialog from "./BancoCordobaRatesDialog";
import {
  getAvailableRubros,
  previewIndividualLiquidation,
  previewIndividualLiquidationPdf,
  storeIndividualLiquidation,
  generateIndividualLiquidationPdf,
  getJudicialInterestRatesLatest,
  importJudicialInterestRatesBcra,
  getIndividualLiquidationDraft,
  saveIndividualLiquidationDraft,
  deleteIndividualLiquidationDraft,
} from "../../services/IndividualLiquidationService";

const CATEGORY_LABELS = { capital: "Capital", honorarios: "Honorarios", gastos: "Gastos" };

// La Municipalidad pide los rubros de Honorarios expresados en JUS en vez de
// pesos. Es por rubro: lo decide si ese rubro está cargado "Por JUS" (no hay
// un toggle general). Solo presentación.
function formatHonorarios(amount, jusValue, active) {
  if (active && Number(jusValue) > 0) {
    return `${(Number(amount) / Number(jusValue)).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} JUS`;
  }
  return formatCurrency(amount);
}
// Sin color por rubro — se muestra todo neutro (como el módulo de municipalidad).
const CATEGORY_COLOR = { capital: "#5f6368", honorarios: "#5f6368", gastos: "#5f6368" };
const CATEGORY_ORDER = ["capital", "gastos", "honorarios"];

// Mantiene los rubros agrupados por categoría (capital → honorarios → gastos).
// Array.prototype.sort es estable: dentro de cada categoría se respeta el orden
// en que se fueron agregando.
const sortByCategory = (list) =>
  [...list].sort((a, b) => CATEGORY_ORDER.indexOf(a.category) - CATEGORY_ORDER.indexOf(b.category));

// Tasa por defecto cuando el rubro admite interés y el juzgado no define una.
const DEFAULT_RATE = "pasiva";

// Tipos de interés elegibles por rubro. Un rubro puede tener varios intereses
// (lista), pero no repetir el mismo tipo.
const INTEREST_RATE_OPTIONS = [
  { value: "pasiva", label: "Tasa Pasiva" },
  { value: "tim", label: "TIM" },
  { value: "banco_cordoba", label: "Bco. Córdoba" },
  { value: "simple", label: "Interés simple" },
];
const INTEREST_RATE_LABELS = INTEREST_RATE_OPTIONS.reduce((a, o) => ({ ...a, [o.value]: o.label }), {});

let IE_SEQ = 0;
const newInterest = (patch = {}) => ({
  key: `ie${++IE_SEQ}`,
  rate_type: patch.rate_type ?? DEFAULT_RATE,
  from: patch.from ?? null,
  to: patch.to ?? null,
  factor: patch.factor ?? 1,
  plus_rate: patch.plus_rate ?? "",
  plus_period: patch.plus_period ?? "mensual",
});

// Series para el visualizador de tasas del encabezado.
const RATE_SERIES = [
  { key: "pasiva", label: "Tasa Pasiva", color: "#1565c0" },
  { key: "tim", label: "TIM (moratorio)", color: "#6a1b9a" },
  { key: "banco_cordoba", label: "Banco de Córdoba", color: "#00838f" },
];

let ROW_SEQ = 0;
const newRowId = () => `r${++ROW_SEQ}`;

// Crea una fila de rubro a partir de un objeto del catálogo (`available` /
// `preload`) o de un rubro sugerido por el comprobante de tasa.
function makeRow(rubro, when) {
  return {
    id: newRowId(),
    individual_liquidation_item_type_id: rubro.individual_liquidation_item_type_id,
    name: rubro.name,
    category: rubro.category,
    allows_interest: rubro.allows_interest !== false,
    // Siempre arranca en "monto fijo"; el multiplicador queda sugerido por si
    // el usuario cambia a "Por JUS".
    amount_mode: "fixed",
    jus_multiplier: rubro.suggested_jus_multiplier != null ? Number(rubro.suggested_jus_multiplier) : null,
    fixed_amount: rubro.suggested_fixed_amount != null ? Number(rubro.suggested_fixed_amount) : null,
    suggested_fixed_amount: rubro.suggested_fixed_amount != null ? Number(rubro.suggested_fixed_amount) : null,
    interests:
      rubro.allows_interest !== false
        ? [
            newInterest({
              rate_type: rubro.default_interest_rate_type || DEFAULT_RATE,
              to: when,
              plus_rate: rubro.default_interest_plus_rate != null ? Number(rubro.default_interest_plus_rate) : "",
              plus_period: rubro.default_interest_plus_period || "mensual",
            }),
          ]
        : [],
    pun_rate_type: rubro.default_punitorio_rate_type || "",
    pun_from: null,
    pun_to: when,
    pun_plus_rate: rubro.default_punitorio_plus_rate != null ? Number(rubro.default_punitorio_plus_rate) : "",
    pun_plus_period: rubro.default_punitorio_plus_period || "mensual",
    pun_factor: 1,
    show_punitorio: !!rubro.default_punitorio_rate_type,
    applies_iva: !!rubro.applies_iva,
    iva_rate: 21,
  };
}

// Aplica los rubros de gastos leídos del comprobante de tasa (Tasa de Justicia /
// Caja de Abogados / Colegio) sobre las filas actuales: setea el importe y la
// fecha «desde» del primer interés. Si el rubro no está, lo agrega. Solo se
// llama con exactamente 3 rubros (validado en el backend).
function mergeTaxRubros(rows, taxRubros, when) {
  if (!Array.isArray(taxRubros) || taxRubros.length !== 3) return rows;
  const next = [...rows];
  taxRubros.forEach((tr) => {
    const from = tr.from ? dayjs(tr.from) : null;
    const amount = Number(tr.fixed_amount) || null;
    // Ojo: si `tr.individual_liquidation_item_type_id` viene null (el parser del
    // comprobante no resolvió un tipo del catálogo), NUNCA debe "matchear" por
    // igualdad contra otro rubro que también tenga id null (p. ej. "Capital",
    // que no es un ítem de catálogo) — eso pisaba el monto/fecha de Capital con
    // los del comprobante. Sin id resuelto, siempre se agrega como rubro nuevo.
    const idx = tr.individual_liquidation_item_type_id
      ? next.findIndex((r) => r.individual_liquidation_item_type_id === tr.individual_liquidation_item_type_id)
      : -1;
    if (idx >= 0) {
      const row = next[idx];
      next[idx] = {
        ...row,
        amount_mode: "fixed",
        fixed_amount: amount ?? row.fixed_amount,
        suggested_fixed_amount: amount ?? row.suggested_fixed_amount,
        interests: (row.interests || []).map((e, i) => (i === 0 && from ? { ...e, from } : e)),
      };
    } else {
      const row = makeRow(tr, when);
      row.fixed_amount = amount;
      row.suggested_fixed_amount = amount;
      if (from && row.interests.length) row.interests[0] = { ...row.interests[0], from };
      next.push(row);
    }
  });
  return next;
}

function fmtApiDate(d) {
  return d && dayjs.isDayjs(d) && d.isValid() ? d.format("YYYY-MM-DD") : undefined;
}

function stripSuffix(concept) {
  return (concept || "Rubro").replace(/\s*\((por JUS[^)]*|monto fijo)\)\s*$/i, "");
}

function rowsFromLiquidation(liq) {
  return (liq?.items ?? []).map((it) => ({
    id: newRowId(),
    individual_liquidation_item_type_id: it.individual_liquidation_item_type_id ?? null,
    name: stripSuffix(it.concept),
    category: it.category,
    // Si el rubro del catálogo dice que no lleva interés, respetarlo. Si el tipo
    // ya no existe, se infiere de si el ítem guardado tenía una tasa.
    allows_interest: it.item_type
      ? it.item_type.allows_interest !== false
      : !!it.interest_rate_type,
    amount_mode: it.amount_mode || "fixed",
    jus_multiplier: it.jus_multiplier != null ? Number(it.jus_multiplier) : null,
    fixed_amount: it.amount_mode === "fixed" ? Number(it.base_amount ?? 0) : null,
    // Lista de intereses. Compat: si el ítem viejo solo tiene columnas sueltas,
    // se arma una lista de un elemento.
    interests: (Array.isArray(it.interests) && it.interests.length
      ? it.interests
      : it.interest_rate_type
        ? [
            {
              rate_type: it.interest_rate_type,
              from: it.interest_from,
              to: it.interest_to,
              factor: it.factor,
              plus_rate: it.interest_plus_rate,
              plus_period: it.interest_plus_period,
            },
          ]
        : []
    ).map((e) =>
      newInterest({
        rate_type: e.rate_type,
        from: e.from ? dayjs(e.from) : null,
        to: e.to ? dayjs(e.to) : null,
        factor: e.factor != null ? Number(e.factor) : 1,
        plus_rate: e.plus_rate != null && e.plus_rate !== "" ? Number(e.plus_rate) : "",
        plus_period: e.plus_period || "mensual",
      }),
    ),
    pun_rate_type: it.punitorio_rate_type || "",
    pun_from: it.punitorio_from ? dayjs(it.punitorio_from) : null,
    pun_to: it.punitorio_to ? dayjs(it.punitorio_to) : null,
    pun_plus_rate: it.punitorio_plus_rate != null && it.punitorio_plus_rate !== "" ? Number(it.punitorio_plus_rate) : "",
    pun_plus_period: it.punitorio_plus_period || "mensual",
    pun_factor: it.punitorio_factor != null ? Number(it.punitorio_factor) : 1,
    show_punitorio: !!it.punitorio_rate_type,
    // No hay columna `applies_iva`: se guarda en metadata. Compat: si no está,
    // se infiere de que haya IVA calculado o de la config del rubro.
    applies_iva:
      it.metadata?.applies_iva != null
        ? !!it.metadata.applies_iva
        : !!it.applies_iva || Number(it.iva_amount) > 0 || (it.item_type?.applies_iva && it.iva_rate != null),
    iva_rate:
      it.metadata?.iva_rate != null && it.metadata.iva_rate !== ""
        ? Number(it.metadata.iva_rate)
        : it.iva_rate != null && it.iva_rate !== ""
          ? Number(it.iva_rate)
          : 21,
  }));
}

// Valida los campos obligatorios de cada rubro. Devuelve un array de mensajes;
// si tiene elementos, la liquidación NO se puede guardar.
function validateRows(rows, calcDate, computed = []) {
  const errors = [];
  rows.forEach((r, idx) => {
    const label = r.name?.trim() || `Rubro ${idx + 1}`;
    const comp = computed[idx];

    if (!r.name || !r.name.trim()) {
      errors.push(`Rubro ${idx + 1}: falta el concepto.`);
    }

    if (r.amount_mode === "jus_multiplier") {
      if (!r.jus_multiplier || Number(r.jus_multiplier) <= 0) {
        errors.push(`«${label}»: ingresá el multiplicador de JUS (mayor a 0).`);
      }
    } else if (!r.fixed_amount || Number(r.fixed_amount) <= 0) {
      errors.push(`«${label}»: ingresá el importe en pesos (mayor a 0).`);
    }

    // Capital y honorarios que admiten interés: al menos una tasa es obligatoria.
    const needsInterest = r.allows_interest && (r.category === "capital" || r.category === "honorarios");
    const interests = r.interests || [];
    if (needsInterest && interests.length === 0) {
      errors.push(`«${label}»: este rubro lleva interés — agregá al menos una tasa (Pasiva, TIM, Bco. Córdoba o interés simple).`);
    }

    // Se permite repetir el mismo tipo de tasa (distintos tramos de fechas).
    interests.forEach((e) => {
      const rtLabel = INTEREST_RATE_LABELS[e.rate_type] || e.rate_type;
      const plusNum = Number(e.plus_rate);
      if (e.rate_type === "simple" && (e.plus_rate === "" || e.plus_rate == null || isNaN(plusNum) || plusNum <= 0)) {
        errors.push(`«${label}» (interés simple): ingresá un porcentaje mayor a 0.`);
      }
      if (e.plus_rate !== "" && e.plus_rate != null && (isNaN(plusNum) || plusNum < 0)) {
        errors.push(`«${label}» (${rtLabel}): el porcentaje no es válido.`);
      }
      if (!Number(e.factor) || Number(e.factor) <= 0) {
        errors.push(`«${label}» (${rtLabel}): el factor debe ser mayor a 0.`);
      }
      if (!e.from || !dayjs.isDayjs(e.from) || !e.from.isValid()) {
        errors.push(`«${label}» (${rtLabel}): falta la fecha «Desde» del interés.`);
        return;
      }
      const to = e.to || calcDate;
      if (to && dayjs.isDayjs(to) && e.from.isAfter(to, "day")) {
        errors.push(`«${label}» (${rtLabel}): la fecha «Desde» es posterior a «Hasta».`);
      }
    });

    // Tasa de serie elegida pero el interés calculado dio $0 (sin tasa publicada, etc.).
    if (comp && Array.isArray(comp.interests)) {
      comp.interests.forEach((ce) => {
        if (ce.rate_type === "simple" || !ce.from) return;
        if (ce.amount != null && !(Number(ce.amount) > 0)) {
          const rtLabel = INTEREST_RATE_LABELS[ce.rate_type] || ce.rate_type;
          errors.push(
            `«${label}» (${rtLabel}): la tasa está seleccionada pero el interés calculado es $0. Revisá las fechas «Desde/Hasta» y que exista tasa publicada para ese período.`,
          );
        }
      });
    }

    if (r.show_punitorio && r.pun_rate_type) {
      if (!r.pun_from || !dayjs.isDayjs(r.pun_from) || !r.pun_from.isValid()) {
        errors.push(`«${label}»: falta la fecha de mora de los punitorios.`);
      } else {
        const pto = r.pun_to || calcDate;
        if (pto && dayjs.isDayjs(pto) && r.pun_from.isAfter(pto, "day")) {
          errors.push(`«${label}»: la fecha de mora es posterior a «Hasta» en los punitorios.`);
        }
      }
      if (r.pun_plus_rate !== "" && r.pun_plus_rate != null && (isNaN(Number(r.pun_plus_rate)) || Number(r.pun_plus_rate) < 0)) {
        errors.push(`«${label}»: el «+ % extra» de los punitorios no es un porcentaje válido.`);
      }
    }

    if (r.applies_iva) {
      const iva = Number(r.iva_rate);
      if (r.iva_rate === "" || isNaN(iva) || iva < 0 || iva > 100) {
        errors.push(`«${label}»: la alícuota de IVA debe estar entre 0 y 100.`);
      }
    }
  });
  return errors;
}

// ── Visualizador de tasas ────────────────────────────────────────────────────
// Muestra los últimos 5 valores cargados de cada serie para que el usuario
// pueda chequear cuál se está tomando y si la serie llega hasta su fecha.

const RatePeek = memo(function RatePeek({ data, calcDate, isDark, canManage, bcraLoading, onImportBcra, onManageBancor }) {
  if (!data) return null;

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: 1.25 }}>
      {RATE_SERIES.map((s) => {
        const serie = data[s.key] ?? {};
        const values = serie.values ?? [];
        const latest = values[0];
        const latestDay = latest ? dayjs(latest.date) : null;
        const covers = latestDay && calcDate ? !latestDay.isBefore(calcDate, "day") : false;
        const isBancor = s.key === "banco_cordoba";

        return (
          <Box
            key={s.key}
            sx={{
              borderRadius: 2,
              border: isDark ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.1)",
              background: isDark ? "rgba(255,255,255,0.03)" : "rgba(255,255,255,0.6)",
              p: 1.4,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.7, mb: 0.9 }}>
              <Box sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: s.color, flexShrink: 0 }} />
              <Typography sx={{ fontSize: 13, fontWeight: 800 }}>{s.label}</Typography>
              <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 0.5 }}>
                {latestDay && (
                  <Box
                    sx={{
                      px: 0.8,
                      py: "2px",
                      borderRadius: 1,
                      fontSize: 10,
                      fontWeight: 900,
                      letterSpacing: 0.3,
                      color: "#fff",
                      bgcolor: covers ? "#2e7d32" : "#ed6c02",
                    }}
                  >
                    {covers ? "AL DÍA" : `HASTA ${latestDay.format("DD/MM")}`}
                  </Box>
                )}
                {canManage &&
                  (isBancor ? (
                    <Tooltip title="Cargar o gestionar la tasa de Banco de Córdoba (carga manual)">
                      <IconButton
                        size="small"
                        onClick={onManageBancor}
                        sx={{ p: 0.35, color: s.color, "&:hover": { bgcolor: `${s.color}18` } }}
                      >
                        <PlaylistAddRoundedIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <Tooltip title={covers ? `${s.label} ya está al día` : `Actualizar ${s.label} desde el BCRA`}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => onImportBcra(s.key)}
                          disabled={bcraLoading || covers}
                          sx={{
                            p: 0.35,
                            color: "#ed6c02",
                            "&:hover": { bgcolor: "#ed6c0218" },
                          }}
                        >
                          {bcraLoading ? <CircularProgress size={14} /> : <CloudDownloadRoundedIcon sx={{ fontSize: 18 }} />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  ))}
              </Box>
            </Box>

            {values.length === 0 ? (
              <Typography sx={{ fontSize: 12.5, opacity: 0.5, fontStyle: "italic" }}>Sin tasas cargadas.</Typography>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3 }}>
                {values.map((v, i) => (
                  <Box
                    key={v.date}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: 12.5,
                      fontWeight: i === 0 ? 800 : 500,
                      opacity: i === 0 ? 1 : 0.62,
                    }}
                  >
                    <Box component="span">{dayjs(v.date).format("DD/MM/YY")}</Box>
                    <Box component="span" sx={{ fontVariantNumeric: "tabular-nums" }}>
                      {isBancor
                        ? v.daily_rate != null
                          ? `${v.daily_rate.toLocaleString("es-AR", { maximumFractionDigits: 2 })}%`
                          : "—"
                        : v.accumulated_index != null
                          ? v.accumulated_index.toLocaleString("es-AR", { maximumFractionDigits: 4 })
                          : "—"}
                    </Box>
                  </Box>
                ))}
              </Box>
            )}

            {latestDay && !covers && (
              <Typography sx={{ fontSize: 11, color: "#ed6c02", mt: 0.75, lineHeight: 1.35 }}>
                No hay tasa para días posteriores al {latestDay.format("DD/MM/YYYY")}; para un «hasta» más nuevo se usa este último valor.
              </Typography>
            )}
            {isBancor && (
              <Typography sx={{ fontSize: 11, opacity: 0.55, mt: 0.6 }}>Carga manual · TNA anual de pizarra.</Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
});

// ── Card de un rubro ─────────────────────────────────────────────────────────

const num4 = (v) => Number(v).toLocaleString("es-AR", { maximumFractionDigits: 4 });
const num2 = (v) => Number(v).toLocaleString("es-AR", { maximumFractionDigits: 2 });

// Compara los valores calculados de un rubro que la UI realmente muestra. Al
// llegar un preview nuevo (`computed` es un array nuevo), los rubros que NO
// cambiaron traen los mismos números → no se re-renderizan.
function sameComputed(a, b) {
  if (a === b) return true;
  if (!a || !b) return false;
  if (
    a.base_amount !== b.base_amount ||
    a.interest_amount !== b.interest_amount ||
    a.punitorio_amount !== b.punitorio_amount ||
    a.iva_amount !== b.iva_amount ||
    a.iva_rate !== b.iva_rate ||
    a.total !== b.total
  ) {
    return false;
  }
  const ai = a.interests || [];
  const bi = b.interests || [];
  if (ai.length !== bi.length) return false;
  for (let i = 0; i < ai.length; i++) {
    if (ai[i]?.amount !== bi[i]?.amount || ai[i]?.index_amount !== bi[i]?.index_amount || ai[i]?.plus_amount !== bi[i]?.plus_amount) {
      return false;
    }
  }
  const am = a.metadata?.interests || [];
  const bm = b.metadata?.interests || [];
  if (am.length !== bm.length) return false;
  for (let i = 0; i < am.length; i++) {
    if (
      am[i]?.update_rate !== bm[i]?.update_rate ||
      am[i]?.index_from !== bm[i]?.index_from ||
      am[i]?.index_to !== bm[i]?.index_to ||
      am[i]?.days !== bm[i]?.days
    ) {
      return false;
    }
  }
  return true;
}

// Una entrada de interés (1..N por rubro). Memoizada + callbacks estables desde
// RubroCard: tipear en una entrada no re-renderiza las demás del mismo rubro.
const InterestEntry = memo(function InterestEntry({
  entry: e,
  idx: k,
  computedEntry: ce,
  metaEntry: me,
  showErrors,
  isDark,
  calcDate,
  onPatch,
  onRemove,
}) {
  const isSimple = e.rate_type === "simple";
  const simpleRateInvalid =
    showErrors && isSimple && (e.plus_rate === "" || e.plus_rate == null || !(Number(e.plus_rate) > 0));
  const fromInvalid = showErrors && (!e.from || !dayjs.isDayjs(e.from) || !e.from.isValid());
  return (
    <Box
      sx={{
        borderRadius: 1.5,
        border: isDark ? "1px solid rgba(255,255,255,0.09)" : "1px solid rgba(0,0,0,0.09)",
        p: 1,
        display: "flex",
        flexDirection: "column",
        gap: 0.75,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, flexWrap: "wrap" }}>
        <ToggleButtonGroup size="small" exclusive value={e.rate_type} onChange={(_, v) => v && onPatch(k, { rate_type: v })}>
          {INTEREST_RATE_OPTIONS.map((opt) => (
            <ToggleButton key={opt.value} value={opt.value} sx={{ px: 1.2, py: 0.4, fontSize: 11.5, fontWeight: 800 }}>
              {opt.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Box sx={{ ml: "auto", textAlign: "right", minWidth: 150 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 900 }}>
            {ce && Number(ce.amount) ? formatCurrency(ce.amount) : "—"}
          </Typography>
          {ce && Number(ce.plus_amount) > 0 && Number(ce.index_amount) > 0 && (
            <Typography sx={{ fontSize: 10, opacity: 0.6 }}>
              índice {formatCurrency(ce.index_amount)} + plus {formatCurrency(ce.plus_amount)}
            </Typography>
          )}
          {!isSimple && me && me.index_from != null && me.index_to != null && (
            <Typography sx={{ fontSize: 10, opacity: 0.6 }}>
              índice {num4(me.index_from)} → {num4(me.index_to)}
            </Typography>
          )}
          {!isSimple && me && Number(me.update_rate) > 0 && (
            <Typography sx={{ fontSize: 10, opacity: 0.6 }}>tasa act. {num2(me.update_rate)}%</Typography>
          )}
          {!isSimple && me && (e.from || e.to) && (
            <Typography sx={{ fontSize: 10, opacity: 0.5 }}>
              {e.from && dayjs.isDayjs(e.from) ? e.from.format("DD/MM/YY") : "—"} →{" "}
              {e.to && dayjs.isDayjs(e.to) ? e.to.format("DD/MM/YY") : "—"}
              {me.days != null ? ` · ${me.days} días` : ""}
            </Typography>
          )}
        </Box>
        <Tooltip title="Quitar este interés">
          <IconButton size="small" onClick={() => onRemove(k)} sx={{ opacity: 0.5, "&:hover": { opacity: 1, color: "error.main" } }}>
            <DeleteOutlineRoundedIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "flex-start", mt: 0.5 }}>
        <DatePicker
          label="Desde"
          value={e.from}
          onChange={(v) => onPatch(k, { from: v })}
          format="DD/MM/YYYY"
          slotProps={{
            textField: { size: "small", sx: { width: 148 }, error: fromInvalid, helperText: fromInvalid ? "Requerido" : " " },
          }}
        />
        <DatePicker
          label="Hasta"
          value={e.to || calcDate}
          onChange={(v) => onPatch(k, { to: v })}
          format="DD/MM/YYYY"
          slotProps={{ textField: { size: "small", sx: { width: 148 }, helperText: " " } }}
        />
        <TextField
          size="small"
          type="number"
          label={isSimple ? "Tasa %" : "+ % extra"}
          value={e.plus_rate}
          onChange={(ev) => onPatch(k, { plus_rate: ev.target.value })}
          required={isSimple}
          error={simpleRateInvalid}
          sx={{ width: 118 }}
          helperText={simpleRateInvalid ? "Requerido" : " "}
          placeholder="0"
          InputProps={{
            startAdornment: isSimple ? undefined : <InputAdornment position="start">+</InputAdornment>,
            endAdornment: <InputAdornment position="end">%</InputAdornment>,
          }}
        />
        <TextField
          select
          size="small"
          label="Período"
          value={e.plus_period || "mensual"}
          onChange={(ev) => onPatch(k, { plus_period: ev.target.value })}
          disabled={!isSimple && !e.plus_rate}
          sx={{ width: 118 }}
          helperText=" "
        >
          <MenuItem value="mensual">Mensual</MenuItem>
          <MenuItem value="anual">Anual</MenuItem>
          <MenuItem value="diario">Diario</MenuItem>
        </TextField>
        <TextField
          size="small"
          type="number"
          label="Factor"
          value={e.factor}
          onChange={(ev) => onPatch(k, { factor: ev.target.value })}
          sx={{ width: 92 }}
          helperText=" "
          InputProps={{
            endAdornment: (
              <Tooltip title={isSimple ? "Multiplica el interés simple. Normalmente 1." : "Multiplica el interés por índice. Normalmente 1."}>
                <InfoOutlinedIcon sx={{ fontSize: 15, opacity: 0.5 }} />
              </Tooltip>
            ),
          }}
        />
      </Box>
      {isSimple && (
        <Typography sx={{ fontSize: 10.5, opacity: 0.55 }}>
          Interés simple = (base × tasa × días) / 100 × factor · días según el período elegido.
        </Typography>
      )}
    </Box>
  );
},
(p, n) =>
  p.entry === n.entry &&
  p.idx === n.idx &&
  p.showErrors === n.showErrors &&
  p.isDark === n.isDark &&
  p.calcDate === n.calcDate &&
  p.onPatch === n.onPatch &&
  p.onRemove === n.onRemove &&
  p.computedEntry?.amount === n.computedEntry?.amount &&
  p.computedEntry?.index_amount === n.computedEntry?.index_amount &&
  p.computedEntry?.plus_amount === n.computedEntry?.plus_amount &&
  p.metaEntry?.update_rate === n.metaEntry?.update_rate &&
  p.metaEntry?.index_from === n.metaEntry?.index_from &&
  p.metaEntry?.index_to === n.metaEntry?.index_to &&
  p.metaEntry?.days === n.metaEntry?.days);

// Memoizado: con muchos rubros/intereses, sin esto cada tecla en CUALQUIER
// campo (de cualquier rubro) volvía a renderizar todas las tarjetas — DatePickers,
// ToggleButtonGroup, etc. de MUI son pesados y eso trababa el diálogo. Con
// memo + callbacks estables (ver setRow/setRowInterests/removeRow), tipear en
// un rubro solo vuelve a renderizar esa tarjeta.
const RubroCard = memo(function RubroCard({ row, computed, calcDate, isDark, showErrors, onChange, onInterests, onRemove, jusValue }) {
  const c = computed ?? {};
  const isHonorarios = row.category === "honorarios";
  // La operadora pidió que la vista en JUS no dependa de un toggle general:
  // es por rubro, según si está cargado "Por JUS" (amount_mode) o "Monto fijo".
  const jusActive = isHonorarios && row.amount_mode === "jus_multiplier" && Number(jusValue) > 0;
  const fmtH = (v) => formatHonorarios(v, jusValue, jusActive);
  const interests = row.interests || [];
  const hasRate = interests.length > 0;
  const singleUpdateRate = interests.length === 1 ? c?.metadata?.interests?.[0]?.update_rate : null;
  const baseInvalid =
    showErrors &&
    (row.amount_mode === "jus_multiplier"
      ? !row.jus_multiplier || Number(row.jus_multiplier) <= 0
      : !row.fixed_amount || Number(row.fixed_amount) <= 0);
  // Capital y honorarios que admiten interés: al menos una tasa es obligatoria.
  const needsInterest = row.allows_interest && (row.category === "capital" || row.category === "honorarios");
  const interestMissing = showErrors && needsInterest && !hasRate;
  const interestZero =
    showErrors &&
    Array.isArray(c.interests) &&
    c.interests.some((ce) => ce.rate_type !== "simple" && ce.from && ce.amount != null && !(Number(ce.amount) > 0));
  // IVA solo tiene sentido en honorarios, o en capital cuando lleva interés
  // (el IVA se calcula sobre los intereses, nunca sobre el capital puro).
  const ivaRelevant = row.category === "honorarios" || (row.category === "capital" && (hasRate || row.show_punitorio));

  const rid = row.id;
  const set = (patch) => onChange(rid, patch);
  // Updates funcionales sobre la lista de intereses (evita perder entradas si se
  // editan varias en el mismo ciclo). Estables para el memo de <InterestEntry>.
  const patchIe = useCallback(
    (k, patch) => onInterests(rid, (list) => list.map((e, i) => (i === k ? { ...e, ...patch } : e))),
    [onInterests, rid],
  );
  const removeIeCb = useCallback(
    (k) => onInterests(rid, (list) => list.filter((_, i) => i !== k)),
    [onInterests, rid],
  );
  // Se pueden agregar varias del mismo tipo (distintos tramos de fechas).
  const addIe = () =>
    onInterests(rid, (list) => [...list, newInterest({ rate_type: list[list.length - 1]?.rate_type || DEFAULT_RATE, to: calcDate })]);

  return (
    <Box
      sx={{
        borderRadius: 2.5,
        border: isDark ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.1)",
        background: isDark ? "rgba(255,255,255,0.03)" : "rgba(255,255,255,0.7)",
        overflow: "hidden",
      }}
    >
      {/* Encabezado */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.75, py: 1.1, borderBottom: isDark ? "1px solid rgba(255,255,255,0.07)" : "1px solid rgba(0,0,0,0.06)" }}>
        <Chip
          size="small"
          label={CATEGORY_LABELS[row.category] ?? row.category}
          sx={{ fontWeight: 800, fontSize: 10, letterSpacing: 0.4, height: 20, bgcolor: `${CATEGORY_COLOR[row.category]}1A`, color: CATEGORY_COLOR[row.category] }}
        />
        <TextField
          variant="standard"
          value={row.name}
          onChange={(e) => set({ name: e.target.value })}
          placeholder="Concepto"
          InputProps={{ disableUnderline: true, sx: { fontSize: 14, fontWeight: 800 } }}
          sx={{ flex: 1 }}
        />
        <Tooltip title="Quitar rubro">
          <IconButton size="small" onClick={() => onRemove(row.id)} sx={{ opacity: 0.5, "&:hover": { opacity: 1, color: "error.main" } }}>
            <DeleteOutlineRoundedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ px: 1.75, py: 1.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
        {/* Monto */}
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5 }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, opacity: 0.55, width: 80 }}>
            Monto
          </Typography>
          <ToggleButtonGroup size="small" exclusive value={row.amount_mode} onChange={(_, v) => v && set({ amount_mode: v })}>
            <ToggleButton value="fixed" sx={{ px: 1.4, py: 0.5, fontSize: 12, fontWeight: 800 }}>
              Monto fijo
            </ToggleButton>
            <ToggleButton value="jus_multiplier" sx={{ px: 1.4, py: 0.5, fontSize: 12, fontWeight: 800 }}>
              Por JUS
            </ToggleButton>
          </ToggleButtonGroup>
          {row.amount_mode === "jus_multiplier" ? (
            <TextField
              size="small"
              type="number"
              label="Multiplicador"
              value={row.jus_multiplier ?? ""}
              onChange={(e) => set({ jus_multiplier: e.target.value === "" ? null : Number(e.target.value) })}
              error={baseInvalid}
              helperText={baseInvalid ? "Requerido" : undefined}
              InputProps={{ startAdornment: <InputAdornment position="start">×</InputAdornment> }}
              sx={{ width: 150 }}
            />
          ) : (
            <Box>
              <CurrencyInput
                size="small"
                label="Importe en pesos"
                value={row.fixed_amount}
                onValueChange={({ floatValue }) => set({ fixed_amount: floatValue ?? null })}
                error={baseInvalid}
                helperText={
                  baseInvalid
                    ? "Requerido"
                    : row.suggested_fixed_amount != null
                      ? "Monto sugerido"
                      : undefined
                }
                sx={{ width: 190 }}
              />
              {row.suggested_fixed_amount != null && Number(row.fixed_amount) !== Number(row.suggested_fixed_amount) && (
                <Button
                  size="small"
                  onClick={() => set({ fixed_amount: Number(row.suggested_fixed_amount) })}
                  sx={{ fontSize: 10.5, textTransform: "none", mt: 0.25, p: 0, minWidth: 0 }}
                >
                  Usar sugerido ({formatCurrency(row.suggested_fixed_amount)})
                </Button>
              )}
            </Box>
          )}
          <Box sx={{ ml: "auto", textAlign: "right" }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, opacity: 0.55 }}>Base</Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 900 }}>
              {c.base_amount != null ? (jusActive ? fmtH(c.base_amount) : formatCurrency(c.base_amount)) : "—"}
            </Typography>
            {jusActive && c.base_amount != null && (
              <Typography sx={{ fontSize: 10.5, opacity: 0.6 }}>{formatCurrency(c.base_amount)}</Typography>
            )}
          </Box>
        </Box>

        {/* Interés — lista (1..N), sin repetir tipo de tasa */}
        {row.allows_interest ? (
          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", gap: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, width: 80, mt: 0.7 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, opacity: 0.55 }}>
                Interés
              </Typography>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, flex: 1, minWidth: 280 }}>
              {interests.length === 0 && (
                <Typography sx={{ fontSize: 11.5, opacity: 0.5, fontStyle: "italic" }}>Sin interés.</Typography>
              )}

              {interests.map((e, k) => (
                <InterestEntry
                  key={e.key}
                  entry={e}
                  idx={k}
                  computedEntry={Array.isArray(c.interests) ? c.interests[k] : null}
                  metaEntry={Array.isArray(c?.metadata?.interests) ? c.metadata.interests[k] : null}
                  showErrors={showErrors}
                  isDark={isDark}
                  calcDate={calcDate}
                  onPatch={patchIe}
                  onRemove={removeIeCb}
                />
              ))}

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Button
                  size="small"
                  startIcon={<AddRoundedIcon sx={{ fontSize: 15 }} />}
                  onClick={addIe}
                  disabled={interests.length >= 12}
                  sx={{ fontSize: 11, textTransform: "none" }}
                >
                  Agregar interés
                </Button>
                {singleUpdateRate != null && (
                  <Typography sx={{ fontSize: 10.5, opacity: 0.55 }}>
                    act. {Number(singleUpdateRate).toLocaleString("es-AR", { maximumFractionDigits: 2 })}%
                  </Typography>
                )}
              </Box>

              {interestMissing && (
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: "error.main" }}>
                  Este rubro lleva interés: agregá al menos una tasa.
                </Typography>
              )}
              {interestZero && (
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: "error.main" }}>
                  Una tasa quedó en $0. Revisá las fechas y que haya tasa publicada para ese período.
                </Typography>
              )}
            </Box>

            <Box sx={{ ml: "auto", textAlign: "right", minWidth: 120, mt: 0.5 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, opacity: 0.55 }}>
                Interés {interests.length > 1 ? `· ${interests.length} tasas` : ""}
              </Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 900, opacity: hasRate ? 1 : 0.4 }}>
                {Number(c.interest_amount)
                  ? (jusActive ? fmtH(c.interest_amount) : formatCurrency(c.interest_amount))
                  : "—"}
              </Typography>
              {jusActive && Number(c.interest_amount) > 0 && (
                <Typography sx={{ fontSize: 10.5, opacity: 0.6 }}>{formatCurrency(c.interest_amount)}</Typography>
              )}
            </Box>
          </Box>
        ) : (
          <Typography sx={{ fontSize: 11, opacity: 0.45, fontStyle: "italic" }}>Este rubro no lleva interés.</Typography>
        )}

        {/* Punitorios (segunda capa de interés sobre la misma base) */}
        {row.allows_interest && (
          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", gap: 1.5 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, opacity: 0.55, width: 80, mt: 0.7 }}>
              Punitorios
            </Typography>
            <Box sx={{ flex: 1, minWidth: 260 }}>
              {!row.show_punitorio ? (
                <Button
                  size="small"
                  startIcon={<AddRoundedIcon sx={{ fontSize: 15 }} />}
                  onClick={() => set({ show_punitorio: true, pun_rate_type: row.pun_rate_type || "pasiva" })}
                  sx={{ fontSize: 11, textTransform: "none" }}
                >
                  Agregar punitorios
                </Button>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <ToggleButtonGroup size="small" exclusive value={row.pun_rate_type || "pasiva"} onChange={(_, v) => v && set({ pun_rate_type: v })}>
                      <ToggleButton value="pasiva" sx={{ px: 1.3, py: 0.5, fontSize: 12, fontWeight: 800 }}>
                        Tasa Pasiva
                      </ToggleButton>
                      <ToggleButton value="tim" sx={{ px: 1.3, py: 0.5, fontSize: 12, fontWeight: 800 }}>
                        TIM
                      </ToggleButton>
                      <ToggleButton value="banco_cordoba" sx={{ px: 1.3, py: 0.5, fontSize: 12, fontWeight: 800 }}>
                        Bco. Córdoba
                      </ToggleButton>
                    </ToggleButtonGroup>
                    <Button size="small" color="error" onClick={() => set({ show_punitorio: false, pun_rate_type: "" })} sx={{ fontSize: 11, textTransform: "none" }}>
                      Quitar
                    </Button>
                  </Box>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "flex-start" }}>
                    <DatePicker
                      label="Fecha de mora"
                      value={row.pun_from}
                      onChange={(v) => set({ pun_from: v })}
                      format="DD/MM/YYYY"
                      slotProps={{ textField: { size: "small", sx: { width: 160 }, error: !row.pun_from, helperText: !row.pun_from ? "Requerido" : " " } }}
                    />
                    <DatePicker
                      label="Hasta"
                      value={row.pun_to || calcDate}
                      onChange={(v) => set({ pun_to: v })}
                      format="DD/MM/YYYY"
                      slotProps={{ textField: { size: "small", sx: { width: 150 }, helperText: " " } }}
                    />
                    <TextField
                      size="small"
                      type="number"
                      label="+ % extra"
                      value={row.pun_plus_rate}
                      onChange={(e) => set({ pun_plus_rate: e.target.value })}
                      sx={{ width: 110 }}
                      helperText=" "
                      InputProps={{
                        startAdornment: <InputAdornment position="start">+</InputAdornment>,
                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                      }}
                    />
                    <TextField
                      select
                      size="small"
                      label="Período"
                      value={row.pun_plus_period || "mensual"}
                      onChange={(e) => set({ pun_plus_period: e.target.value })}
                      disabled={!row.pun_plus_rate}
                      sx={{ width: 120 }}
                      helperText=" "
                    >
                      <MenuItem value="mensual">Mensual</MenuItem>
                      <MenuItem value="anual">Anual</MenuItem>
                      <MenuItem value="diario">Diario</MenuItem>
                    </TextField>
                  </Box>
                </Box>
              )}
            </Box>
            <Box sx={{ ml: "auto", textAlign: "right", minWidth: 110, mt: 0.5 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, opacity: 0.55 }}>Punitorios</Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 900, opacity: row.show_punitorio ? 1 : 0.4 }}>
                {Number(c.punitorio_amount) ? formatCurrency(c.punitorio_amount) : "—"}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Total del rubro */}
        <Divider sx={{ opacity: 0.5 }} />
        <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          {ivaRelevant && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mr: "auto" }}>
              <Tooltip
                title={
                  row.category === "capital"
                    ? "IVA sobre los intereses del capital (nunca sobre el capital)"
                    : "IVA sobre honorarios + intereses (Responsable Inscripto)"
                }
              >
                <Chip
                  size="small"
                  label={row.applies_iva ? "IVA ✓" : "IVA"}
                  variant={row.applies_iva ? "filled" : "outlined"}
                  color={row.applies_iva ? "secondary" : "default"}
                  onClick={() => set({ applies_iva: !row.applies_iva })}
                  sx={{ fontWeight: 800, fontSize: 10, cursor: "pointer" }}
                />
              </Tooltip>
              <TextField
                size="small"
                type="number"
                label="Alícuota IVA"
                value={row.iva_rate}
                onChange={(e) => set({ iva_rate: e.target.value })}
                disabled={!row.applies_iva}
                sx={{ width: 130 }}
                inputProps={{ min: 0, max: 100, step: 0.5, style: { textAlign: "right" } }}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
              />
              {Number(c.iva_amount) > 0 && (
                <Typography sx={{ fontSize: 11, opacity: 0.7 }}>
                  = {formatCurrency(c.iva_amount)}
                  {jusActive && ` · ${fmtH(c.iva_amount)}`}
                </Typography>
              )}
            </Box>
          )}
          <Typography sx={{ fontSize: 12, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.5, opacity: 0.55 }}>Total rubro</Typography>
          <Typography sx={{ fontSize: 15, fontWeight: 900, color: "primary.main" }}>
            {c.total != null ? formatCurrency(c.total) : "—"}
          </Typography>
          {jusActive && c.total != null && (
            <Typography sx={{ fontSize: 10.5, opacity: 0.6 }}>{fmtH(c.total)}</Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
},
(p, n) =>
  p.row === n.row &&
  p.showErrors === n.showErrors &&
  p.calcDate === n.calcDate &&
  p.isDark === n.isDark &&
  p.onChange === n.onChange &&
  p.onInterests === n.onInterests &&
  p.onRemove === n.onRemove &&
  p.jusValue === n.jusValue &&
  sameComputed(p.computed, n.computed));

// ── Borrador compartido (no se pierde lo que se va cargando) ─────────────────
// Se guarda en la base de datos por expediente (tabla individual_liquidation_drafts),
// visible para cualquier usuario que entre a este mismo expediente — ya no vive
// en el localStorage de un solo navegador.

const dstr = (d) => (d && dayjs.isDayjs(d) && d.isValid() ? d.toISOString() : null);
const dparse = (s) => (s ? dayjs(s) : null);

// Fechas → ISO string, para mandar al backend.
function dehydrateDraftRows(rows) {
  return (rows || []).map((r) => ({
    ...r,
    interests: (r.interests || []).map((e) => ({ ...e, from: dstr(e.from), to: dstr(e.to) })),
    pun_from: dstr(r.pun_from),
    pun_to: dstr(r.pun_to),
  }));
}

// ISO string → dayjs, al traer el borrador guardado.
function hydrateDraftRows(rows) {
  return (rows || []).map((r) => ({
    ...r,
    interests: (r.interests || []).map((e) => ({ ...e, from: dparse(e.from), to: dparse(e.to) })),
    pun_from: dparse(r.pun_from),
    pun_to: dparse(r.pun_to),
  }));
}

// ── Dialog ───────────────────────────────────────────────────────────────────

export default function IndividualLiquidationFormDialog({ open, onClose, legalFile, editingLiquidation, onSaved }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  // Los botones usan rutas del grupo `catalog` (+ el permiso extra del import BCRA).
  const canManageRates =
    hasPermission("administrar catalogos liquidacion comun") && hasPermission("tasas interes judiciales");
  const topRef = useRef(null);

  const glassPanel = {
    background: isDark ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.75)",
    border: isDark ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.08)",
    borderRadius: 2.5,
  };

  const [calcDate, setCalcDate] = useState(dayjs());
  const [jusValue, setJusValue] = useState(null);
  const [notes, setNotes] = useState("");
  const [rows, setRows] = useState([]);
  // Valor JUS resuelto por el backend en el último preview (si `jusValue` se
  // deja vacío, el servidor usa el vigente — hace falta para mostrar la
  // conversión en el formulario).
  const [resolvedJusValue, setResolvedJusValue] = useState(null);

  const [available, setAvailable] = useState([]);
  const [ratePeek, setRatePeek] = useState(null);
  const [showRates, setShowRates] = useState(true);
  const [bcraLoading, setBcraLoading] = useState(false);
  const [bancorOpen, setBancorOpen] = useState(false);

  const [computed, setComputed] = useState([]);
  const [totals, setTotals] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);

  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [taxOpen, setTaxOpen] = useState(false);
  const [liquidationSheetOpen, setLiquidationSheetOpen] = useState(false);

  // Vista previa del PDF antes de guardar.
  const [pdfOpen, setPdfOpen] = useState(false);
  const [pdfHtml, setPdfHtml] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  // Borrador local
  const [draftInfo, setDraftInfo] = useState(null); // draft parseado esperando decisión
  const [draftSavedAt, setDraftSavedAt] = useState(null);

  const debounceRef = useRef(null);
  const draftDebounceRef = useRef(null);

  const refreshRatePeek = useCallback(() => {
    getJudicialInterestRatesLatest()
      .then((r) => setRatePeek(r.data?.data ?? null))
      .catch(() => setRatePeek(null));
  }, []);

  useEffect(() => {
    if (!open || !legalFile) return;
    ROW_SEQ = 0;
    setCalcDate(dayjs());
    setJusValue(editingLiquidation?.jus_value != null ? Number(editingLiquidation.jus_value) : null);
    setNotes(editingLiquidation?.notes ?? "");
    setRows(editingLiquidation ? sortByCategory(rowsFromLiquidation(editingLiquidation)) : []);
    setComputed([]);
    setTotals(null);
    setWarnings([]);
    setSaveError("");
    setShowValidation(false);
    setDraftSavedAt(null);

    // ¿Hay un borrador compartido sin guardar de este expediente? (puede
    // haberlo dejado otro usuario, no solo este navegador).
    setDraftInfo(null);
    getIndividualLiquidationDraft(legalFile.id, editingLiquidation?.id)
      .then((res) => {
        const d = res.data?.data;
        if (!d || !(d.rows?.length > 0)) return;
        const editedAt = editingLiquidation?.updated_at ? new Date(editingLiquidation.updated_at).getTime() : 0;
        const savedAt = d.updated_at ? new Date(d.updated_at).getTime() : 0;
        if (savedAt <= editedAt) return;
        setDraftInfo({
          savedAt,
          calcDate: dparse(d.calc_date) || dayjs(),
          jusValue: d.jus_value ?? null,
          notes: d.notes ?? "",
          rows: hydrateDraftRows(d.rows),
          updatedBy: d.updated_by ?? null,
        });
      })
      .catch(() => {
        /* sin borrador / falla de red — no bloquea abrir el formulario */
      });

    getAvailableRubros(legalFile.id)
      .then((r) => {
        // `rubros` = catálogo completo (siempre se pueden agregar otros rubros,
        // aunque el juzgado no los tenga configurados).
        setAvailable(r.data?.rubros ?? []);
        // `preload` = rubros que se precargan al abrir (config del juzgado, o
        // los `is_default` si no hay config). Solo en liquidaciones nuevas.
        const preload = r.data?.preload ?? [];
        if (!editingLiquidation && preload.length) {
          setRows(sortByCategory(preload.map((rubro) => makeRow(rubro, dayjs()))));
        }
        // Alta nueva: si el comprobante de tasa trae exactamente 3 conceptos,
        // agrega/completa Tasa de Justicia + Caja + Colegio con importe y fecha.
        if (!editingLiquidation) {
          const taxRubros = r.data?.tax_receipt_rubros ?? [];
          if (taxRubros.length === 3) {
            setRows((prev) => sortByCategory(mergeTaxRubros(prev, taxRubros, dayjs())));
          }
        }
        if (editingLiquidation) {
          // Al editar: reconciliar `allows_interest` de cada rubro contra el
          // catálogo (autoritativo) para no forzar interés donde no corresponde.
          const byId = new Map(
            (r.data?.rubros ?? []).map((x) => [x.individual_liquidation_item_type_id, x]),
          );
          setRows((prev) =>
            prev.map((row) => {
              const cat = byId.get(row.individual_liquidation_item_type_id);
              return cat ? { ...row, allows_interest: cat.allows_interest !== false } : row;
            }),
          );
        }
      })
      .catch(() => setAvailable([]));

    refreshRatePeek();
  }, [open, legalFile, editingLiquidation, refreshRatePeek]);

  // useCallback: se pasa a <RatePeek> (memoizado); si cambiara de identidad
  // en cada render, el memo no serviría.
  const handleImportBcra = useCallback(
    async (rateType) => {
      setBcraLoading(true);
      try {
        await importJudicialInterestRatesBcra(rateType ? { rate_type: rateType } : {});
        const label = rateType === "tim" ? "TIM" : rateType === "pasiva" ? "Tasa Pasiva" : "Tasa Pasiva y TIM";
        showToast(`Actualización de ${label} encolada desde el BCRA. Se te notificará al finalizar.`, "success");
      } catch (e) {
        showToast(e?.response?.data?.message ?? "No se pudo encolar la actualización.", "error");
      } finally {
        setBcraLoading(false);
      }
    },
    [showToast],
  );

  const openBancor = useCallback(() => setBancorOpen(true), []);


  const buildPayload = useCallback(
    () => ({
      calculation_date: fmtApiDate(calcDate),
      jus_value: jusValue ?? undefined,
      notes: notes || undefined,
      items: rows.map((r, idx) => ({
        individual_liquidation_item_type_id: r.individual_liquidation_item_type_id ?? undefined,
        concept: r.name || undefined,
        category: r.category,
        amount_mode: r.amount_mode,
        jus_multiplier: r.amount_mode === "jus_multiplier" ? Number(r.jus_multiplier) || 0 : undefined,
        fixed_amount: r.amount_mode === "fixed" ? Number(r.fixed_amount) || 0 : undefined,
        interests: (r.interests || []).map((e) => ({
          rate_type: e.rate_type,
          from: fmtApiDate(e.from),
          to: fmtApiDate(e.to || calcDate),
          factor: Number(e.factor) || 1,
          plus_rate: e.plus_rate !== "" && e.plus_rate != null ? Number(e.plus_rate) : undefined,
          plus_period:
            e.rate_type === "simple" || (e.plus_rate !== "" && e.plus_rate != null)
              ? e.plus_period || "mensual"
              : undefined,
        })),
        punitorio_rate_type: r.pun_rate_type || undefined,
        punitorio_from: r.pun_rate_type ? fmtApiDate(r.pun_from) : undefined,
        punitorio_to: r.pun_rate_type ? fmtApiDate(r.pun_to || calcDate) : undefined,
        punitorio_factor: r.pun_rate_type ? Number(r.pun_factor) || 1 : undefined,
        punitorio_plus_rate: r.pun_rate_type && r.pun_plus_rate !== "" && r.pun_plus_rate != null ? Number(r.pun_plus_rate) : undefined,
        punitorio_plus_period: r.pun_rate_type && r.pun_plus_rate !== "" && r.pun_plus_rate != null ? r.pun_plus_period || "mensual" : undefined,
        applies_iva: r.applies_iva,
        iva_rate: r.applies_iva ? Number(r.iva_rate) || 21 : undefined,
        allows_interest: r.allows_interest !== false,
        sort_order: idx,
      })),
    }),
    [calcDate, jusValue, notes, rows],
  );

  // Pide el preview y devuelve los datos frescos (además de volcarlos al estado).
  const fetchPreview = useCallback(async () => {
    if (!legalFile || rows.length === 0) {
      setComputed([]);
      setTotals(null);
      setWarnings([]);
      return { items: [], totals: null, warnings: [] };
    }
    setPreviewLoading(true);
    try {
      const res = await previewIndividualLiquidation(legalFile.id, buildPayload());
      const data = {
        items: res.data?.items ?? [],
        totals: res.data?.totals ?? null,
        warnings: res.data?.warnings ?? [],
      };
      setComputed(data.items);
      setTotals(data.totals);
      setWarnings(data.warnings);
      setResolvedJusValue(res.data?.jus_value ?? null);
      return data;
    } catch {
      const data = { items: [], totals: null, warnings: ["No se pudo recalcular la liquidación."] };
      setWarnings(data.warnings);
      return data;
    } finally {
      setPreviewLoading(false);
    }
  }, [legalFile, rows.length, buildPayload]);

  const runPreview = useCallback(() => {
    fetchPreview();
  }, [fetchPreview]);

  useEffect(() => {
    if (!open) return;
    clearTimeout(debounceRef.current);
    // 600ms: en máquinas lentas cada preview dispara un re-cálculo + re-render;
    // conviene esperar un poco más a que el usuario termine de tipear.
    debounceRef.current = setTimeout(runPreview, 600);
    return () => clearTimeout(debounceRef.current);
  }, [open, runPreview]);

  // Autosave del borrador compartido (mientras no haya un prompt de "retomar" abierto).
  useEffect(() => {
    if (!open || !legalFile || draftInfo) return;
    if (rows.length === 0) return;
    clearTimeout(draftDebounceRef.current);
    draftDebounceRef.current = setTimeout(() => {
      saveIndividualLiquidationDraft(legalFile.id, {
        editing_liquidation_id: editingLiquidation?.id ?? null,
        calc_date: fmtApiDate(calcDate) ?? null,
        jus_value: jusValue ?? null,
        notes: notes || null,
        rows: dehydrateDraftRows(rows),
      })
        .then((res) => setDraftSavedAt(res.data?.updated_at ? new Date(res.data.updated_at).getTime() : Date.now()))
        .catch(() => {
          /* autosave best-effort — no interrumpe al usuario si falla */
        });
    }, 1500);
    return () => clearTimeout(draftDebounceRef.current);
  }, [open, legalFile, editingLiquidation, draftInfo, rows, calcDate, jusValue, notes]);

  const clearDraft = useCallback(() => {
    if (legalFile) {
      deleteIndividualLiquidationDraft(legalFile.id, editingLiquidation?.id).catch(() => {});
    }
    setDraftSavedAt(null);
  }, [legalFile, editingLiquidation]);

  const resumeDraft = () => {
    if (!draftInfo) return;
    setRows(draftInfo.rows);
    setCalcDate(draftInfo.calcDate);
    setJusValue(draftInfo.jusValue);
    setNotes(draftInfo.notes);
    setDraftInfo(null);
  };

  const discardDraft = () => {
    clearDraft();
    setDraftInfo(null);
  };

  // Referencias estables (sin cerrar sobre `rows`/`calcDate`) para que
  // React.memo en RubroCard funcione: si estas funciones cambiaran en cada
  // render, memo sería inútil (props "distintas" siempre).
  const setRow = useCallback(
    (id, patch) => setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r))),
    [],
  );
  const setRowInterests = useCallback(
    (id, fn) => setRows((prev) => prev.map((r) => (r.id === id ? { ...r, interests: fn(r.interests || []) } : r))),
    [],
  );
  const removeRow = useCallback((id) => setRows((prev) => prev.filter((r) => r.id !== id)), []);

  const addRubro = (rubro) => setRows((prev) => sortByCategory([...prev, makeRow(rubro, calcDate)]));

  const applyTaxRubros = useCallback(
    (taxRubros) => setRows((prev) => sortByCategory(mergeTaxRubros(prev, taxRubros, calcDate))),
    [calcDate],
  );

  // Errores que impiden guardar. Solo se recalculan cuando el usuario ya
  // intentó guardar (showValidation): así `validateRows` no corre en cada
  // tecla mientras se carga la planilla.
  const validationErrors = useMemo(
    () => (showValidation ? validateRows(rows, calcDate, computed) : []),
    [showValidation, rows, calcDate, computed],
  );

  const handleSaveClick = async () => {
    if (rows.length === 0) return;
    setSaveError("");
    // Recalcula con datos frescos para validar el interés efectivamente calculado.
    const fresh = await fetchPreview();
    const errs = validateRows(rows, calcDate, fresh.items);
    if (errs.length > 0) {
      setShowValidation(true);
      showToast(
        errs.length === 1
          ? "Falta completar un dato obligatorio para guardar."
          : `Faltan ${errs.length} datos obligatorios para guardar.`,
        "error",
      );
      requestAnimationFrame(() => topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
      return;
    }
    setShowValidation(false);
    // Las notas «ⓘ» (se usó la tasa del día anterior) son informativas: no bloquean.
    const blocking = (fresh.warnings ?? []).filter((w) => !w.trim().startsWith("ⓘ"));
    if (blocking.length > 0) {
      setConfirmOpen(true);
      return;
    }
    doSave();
  };

  const handlePreviewPdf = async () => {
    if (rows.length === 0) return;
    setPdfLoading(true);
    setPdfHtml(null);
    setPdfOpen(true);
    try {
      const res = await previewIndividualLiquidationPdf(legalFile.id, buildPayload());
      setPdfHtml(res.data?.html ?? null);
    } catch (e) {
      setPdfOpen(false);
      showToast(e?.response?.data?.message ?? "No se pudo generar la vista previa del PDF.", "error");
    } finally {
      setPdfLoading(false);
    }
  };

  const doSave = async () => {
    setConfirmOpen(false);
    setSaveLoading(true);
    setSaveError("");
    try {
      const res = await storeIndividualLiquidation(legalFile.id, buildPayload());
      const id = res.data?.id;
      if (id) {
        try {
          await generateIndividualLiquidationPdf(id);        } catch {
          /* PDF no bloqueante */
        }
      }
      clearDraft();
      onSaved?.();
      onClose();
    } catch (e) {
      setSaveError(e?.response?.data?.message ?? "Error al guardar la liquidación.");
    } finally {
      setSaveLoading(false);
    }
  };

  // Catálogo ordenado por categoría (capital → honorarios → gastos) y nombre,
  // para el autocomplete de "agregar rubro".
  const addOptions = useMemo(
    () =>
      [...available].sort(
        (a, b) =>
          CATEGORY_ORDER.indexOf(a.category) - CATEGORY_ORDER.indexOf(b.category) ||
          (a.name || "").localeCompare(b.name || ""),
      ),
    [available],
  );

  // Rubros ya agregados (para el estado de los checkbox del autocomplete).
  const selectedRubros = useMemo(() => {
    const usedIds = new Set(rows.map((r) => r.individual_liquidation_item_type_id).filter(Boolean));
    return addOptions.filter((o) => usedIds.has(o.individual_liquidation_item_type_id));
  }, [addOptions, rows]);

  const handleRubrosChange = (_e, newValue) => {
    const newIds = new Set(newValue.map((o) => o.individual_liquidation_item_type_id));
    const curIds = new Set(rows.map((r) => r.individual_liquidation_item_type_id).filter(Boolean));

    newValue.forEach((o) => {
      if (!curIds.has(o.individual_liquidation_item_type_id)) addRubro(o);
    });

    const removed = [...curIds].filter((id) => !newIds.has(id));
    if (removed.length) {
      setRows((prev) => prev.filter((r) => !removed.includes(r.individual_liquidation_item_type_id)));
    }
  };

  const alertWarnings = warnings.filter((w) => w.trim().startsWith("⚠"));
  // El total de Honorarios va en JUS si hay al menos un rubro "Por JUS". Si
  // toda la planilla de honorarios está a "Monto fijo", el total va en pesos.
  const honorariosHasJusRow = rows.some((r) => r.category === "honorarios" && r.amount_mode === "jus_multiplier");

  return (
    <>
    <AppDialog
      open={open}
      onClose={onClose}
      title={editingLiquidation ? "Nueva versión — Liquidación común" : "Nueva liquidación común"}
      subtitle={legalFile ? `Expediente ${legalFile.code}${legalFile.dependencies ? ` · ${legalFile.dependencies}` : ""}` : ""}
      maxWidth="lg"
      fullWidth
      fullScreenMobile
      stickyHeader
      actionsSx={{ flexWrap: "wrap", rowGap: 1.25 }}
      actions={
        <>
          <Box sx={{ mr: { xs: 0, md: "auto" }, width: { xs: "100%", md: "auto" }, display: "flex", alignItems: "center", gap: 2.5, flexWrap: "wrap", minWidth: 0 }}>
            {previewLoading && <CircularProgress size={18} />}
            {totals && (
              // El desglose por rubro ya se ve en el cuerpo del diálogo — en
              // pantallas chicas solo el total general, para no amontonar.
              <Box sx={{ display: { xs: "none", sm: "flex" }, alignItems: "flex-start", gap: 2.5, flexWrap: "wrap" }}>
                {[
                  { key: "capital", label: "Capital", subtotal: Number(totals.capital) || 0, iva: Number(totals.iva_capital) || 0 },
                  { key: "gastos", label: "Gastos", subtotal: Number(totals.gastos) || 0, iva: Number(totals.iva_gastos) || 0 },
                  { key: "honorarios", label: "Honorarios", subtotal: Number(totals.honorarios) || 0, iva: Number(totals.iva_honorarios) || 0 },
                ].map(({ key, label, subtotal, iva }) => (
                  <Box key={key} sx={{ display: "flex", flexDirection: "column", lineHeight: 1.25 }}>
                    <Typography sx={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, opacity: 0.5, display: "flex", alignItems: "center", gap: 0.4 }}>
                      {label}
                      {key === "honorarios" && honorariosHasJusRow && (
                        <Tooltip title="El valor en JUS está redondeado para mostrar. El monto exacto y válido es el que figura en pesos — no multiplicar el JUS redondeado por el valor de JUS para verificar, puede no coincidir.">
                          <InfoOutlinedIcon sx={{ fontSize: 12, opacity: 0.5 }} />
                        </Tooltip>
                      )}
                    </Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 800 }}>
                      {key === "honorarios" ? formatHonorarios(subtotal, jusValue ?? resolvedJusValue, honorariosHasJusRow) : formatCurrency(subtotal)}
                    </Typography>
                    <Typography sx={{ fontSize: 11, opacity: 0.6 }}>
                      + IVA {iva > 0 ? (key === "honorarios" ? formatHonorarios(iva, jusValue ?? resolvedJusValue, honorariosHasJusRow) : formatCurrency(iva)) : "—"}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
            {totals && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  lineHeight: 1.25,
                  pl: { xs: 0, sm: 2.5 },
                  borderLeft: { xs: "none", sm: isDark ? "1px solid rgba(255,255,255,0.16)" : "1px solid rgba(0,0,0,0.16)" },
                }}
              >
                <Typography sx={{ fontSize: 11.5, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.6, opacity: 0.6 }}>
                  Total general
                </Typography>
                <Typography sx={{ fontSize: { xs: 18, sm: 22 }, fontWeight: 900, color: "primary.main", letterSpacing: -0.5 }}>
                  {formatCurrency(totals.grand_total)}
                </Typography>
                <Typography sx={{ fontSize: 11, opacity: 0.6 }}>
                  IVA total {formatCurrency(Number(totals.iva) || 0)}
                </Typography>
              </Box>
            )}
          </Box>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", width: { xs: "100%", md: "auto" } }}>
            <Button
              variant="contained"
              onClick={onClose}
              disabled={saveLoading}
              startIcon={<CloseRoundedIcon />}
              sx={{ borderRadius: 2.5, textTransform: "none", fontWeight: 800, px: 2.2, flex: { xs: "1 1 0", md: "0 0 auto" } }}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handlePreviewPdf}
              disabled={pdfLoading || saveLoading || rows.length === 0}
              startIcon={pdfLoading ? <CircularProgress size={15} color="inherit" /> : <PictureAsPdfRoundedIcon />}
              sx={{ borderRadius: 2.5, textTransform: "none", fontWeight: 800, px: 2.2, flex: { xs: "1 1 0", md: "0 0 auto" }, whiteSpace: "nowrap" }}
            >
              {pdfLoading ? "Generando…" : "Vista previa"}
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveClick}
              disabled={saveLoading || rows.length === 0}
              startIcon={saveLoading ? <CircularProgress size={15} color="inherit" /> : <SaveRoundedIcon />}
              sx={{ borderRadius: 2.5, textTransform: "none", fontWeight: 900, px: 2.6, flex: { xs: "1 1 0", md: "0 0 auto" }, whiteSpace: "nowrap" }}
            >
              {saveLoading ? "Guardando…" : "Guardar"}
            </Button>
          </Box>
        </>
      }
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, p: 0.5 }}>
        <div ref={topRef} style={{ height: 0 }} aria-hidden />
        {draftSavedAt && (
          <Typography sx={{ fontSize: 11, opacity: 0.55, textAlign: "right", mt: -0.5 }}>
            Borrador guardado {dayjs(draftSavedAt).format("HH:mm")}
          </Typography>
        )}
        {/* Parámetros */}
        <Box sx={{ ...glassPanel, p: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.6, opacity: 0.5 }}>
              Parámetros de cálculo
            </Typography>
            <Button
              size="small"
              startIcon={<DescriptionRoundedIcon sx={{ fontSize: 16 }} />}
              onClick={() => setLiquidationSheetOpen(true)}
              sx={{ ml: "auto", fontSize: 12, fontWeight: 700, textTransform: "none", borderRadius: 2 }}
            >
              Planilla del portal
            </Button>
            <Button
              size="small"
              startIcon={<ReceiptLongRoundedIcon sx={{ fontSize: 16 }} />}
              onClick={() => setTaxOpen(true)}
              sx={{ fontSize: 12, fontWeight: 700, textTransform: "none", borderRadius: 2 }}
            >
              Comprobantes de tasa
            </Button>
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
            <DatePicker
              label="Fecha de cálculo"
              value={calcDate}
              onChange={(v) => setCalcDate(v || dayjs())}
              slotProps={{ textField: { size: "small", fullWidth: true, helperText: "Se usa como “hasta” por defecto en cada rubro" } }}
              format="DD/MM/YYYY"
            />
            <CurrencyInput
              label="Valor JUS (opcional)"
              value={jusValue}
              onValueChange={({ floatValue }) => setJusValue(floatValue ?? null)}
              size="small"
              fullWidth
              helperText="Si se deja vacío se usa el valor JUS vigente"
            />
          </Box>

          {ratePeek && (
            <Box
              sx={{
                mt: 2,
                pt: 1.75,
                borderTop: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: showRates ? 1.25 : 0 }}>
                <ShowChartRoundedIcon sx={{ fontSize: 17, opacity: 0.55 }} />
                <Typography sx={{ fontSize: 12, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.6, opacity: 0.5 }}>
                  Tasas de interés cargadas · últimos 5 valores
                </Typography>
                <Button
                  size="small"
                  onClick={() => setShowRates((s) => !s)}
                  sx={{ ml: "auto", fontSize: 12, fontWeight: 700, textTransform: "none", minWidth: 0 }}
                >
                  {showRates ? "Ocultar" : "Mostrar"}
                </Button>
              </Box>
              <Collapse in={showRates} unmountOnExit>
                <RatePeek
                  data={ratePeek}
                  calcDate={calcDate}
                  isDark={isDark}
                  canManage={canManageRates}
                  bcraLoading={bcraLoading}
                  onImportBcra={handleImportBcra}
                  onManageBancor={openBancor}
                />

                <Typography sx={{ fontSize: 11, opacity: 0.55, mt: 1, lineHeight: 1.4 }}>
                  El interés de cada rubro usa la serie que elijas: índice del día previo a «desde» y el del «hasta».
                  Verificá que la serie llegue hasta tu fecha de cálculo — si no, se toma el último valor disponible.
                </Typography>
              </Collapse>
            </Box>
          )}
        </Box>

        {/* Datos obligatorios faltantes — bloquean el guardado */}
        {showValidation && validationErrors.length > 0 && (
          <Alert severity="error" sx={{ borderRadius: 2 }} onClose={() => setShowValidation(false)}>
            <AlertTitle sx={{ fontWeight: 800 }}>
              No se puede guardar — {validationErrors.length}{" "}
              {validationErrors.length === 1 ? "dato obligatorio" : "datos obligatorios"} sin completar
            </AlertTitle>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {validationErrors.map((e, i) => (
                <li key={i}>
                  <Typography variant="body2">{e}</Typography>
                </li>
              ))}
            </Box>
          </Alert>
        )}

        {/* Advertencias: ⚠ = rojo (bloquea), ⓘ = azul info (día anterior, normal), resto = ámbar */}
        {warnings.length > 0 && (() => {
          const alerts = warnings.filter((w) => w.trim().startsWith("⚠"));
          const infos = warnings.filter((w) => w.trim().startsWith("ⓘ"));
          const notes = warnings.filter((w) => !w.trim().startsWith("⚠") && !w.trim().startsWith("ⓘ"));
          return (
            <>
              {alerts.length > 0 && (
                <Alert severity="error" sx={{ borderRadius: 2 }}>
                  <AlertTitle sx={{ fontWeight: 800 }}>Tasa de interés no publicada</AlertTitle>
                  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                    {alerts.map((w, i) => (
                      <li key={i}>
                        <Typography variant="body2">{w.replace(/^⚠\s*/, "")}</Typography>
                      </li>
                    ))}
                  </Box>
                </Alert>
              )}
              {notes.length > 0 && (
                <Alert severity="warning" sx={{ borderRadius: 2 }}>
                  <AlertTitle sx={{ fontWeight: 800 }}>Revisá estas tasas</AlertTitle>
                  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                    {notes.map((w, i) => (
                      <li key={i}>
                        <Typography variant="body2">{w}</Typography>
                      </li>
                    ))}
                  </Box>
                </Alert>
              )}
              {infos.length > 0 && (
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  <AlertTitle sx={{ fontWeight: 800 }}>Se usó la tasa del día anterior</AlertTitle>
                  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                    {infos.map((w, i) => (
                      <li key={i}>
                        <Typography variant="body2">{w.replace(/^ⓘ\s*/, "")}</Typography>
                      </li>
                    ))}
                  </Box>
                </Alert>
              )}
            </>
          );
        })()}

        {/* Agregar rubro — buscador con checkboxes */}
        <Box sx={{ ...glassPanel, p: 1.5, display: "flex", alignItems: "center", gap: 1.25 }}>
          <AddRoundedIcon sx={{ fontSize: 18, opacity: 0.55, flexShrink: 0 }} />
          <Autocomplete
            multiple
            fullWidth
            size="small"
            disableCloseOnSelect
            openOnFocus
            options={addOptions}
            value={selectedRubros}
            onChange={handleRubrosChange}
            groupBy={(o) => CATEGORY_LABELS[o.category] ?? o.category}
            getOptionLabel={(o) => o.name || ""}
            isOptionEqualToValue={(o, v) => o.individual_liquidation_item_type_id === v.individual_liquidation_item_type_id}
            renderTags={() => null}
            noOptionsText="Sin rubros en el catálogo"
            renderOption={(props, option, { selected }) => (
              <li {...props} key={option.individual_liquidation_item_type_id}>
                <Checkbox
                  size="small"
                  icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  checked={selected}
                  sx={{ mr: 1, p: 0.5 }}
                />
                <Box component="span" sx={{ fontSize: 13, fontWeight: 600 }}>
                  {option.name}
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Agregar rubro"
                placeholder={rows.length ? "Buscar y agregar otro rubro…" : "Buscar y agregar rubro…"}
              />
            )}
          />
          <Typography sx={{ fontSize: 12.5, opacity: 0.55, fontWeight: 600, whiteSpace: "nowrap", flexShrink: 0 }}>
            {rows.length} {rows.length === 1 ? "rubro" : "rubros"}
          </Typography>
        </Box>

        {/* Rubros */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography sx={{ fontSize: 12, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.6, opacity: 0.5 }}>
              Rubros de la liquidación
            </Typography>
            <Typography sx={{ fontSize: 12.5, opacity: 0.55, fontWeight: 600 }}>
              {rows.length} {rows.length === 1 ? "rubro" : "rubros"}
            </Typography>
          </Box>

          {rows.length === 0 && (
            <Box sx={{ ...glassPanel, p: 3, textAlign: "center" }}>
              <Typography sx={{ fontWeight: 800, mb: 0.5 }}>Todavía no agregaste rubros</Typography>
              <Typography variant="body2" sx={{ opacity: 0.6 }}>
                Elegí un rubro arriba, poné el monto (por JUS o en pesos) y si lleva interés.
              </Typography>
            </Box>
          )}

          {rows.map((row, idx) => {
            const firstOfCategory = idx === 0 || rows[idx - 1].category !== row.category;
            return (
              <Fragment key={row.id}>
                {firstOfCategory && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: idx === 0 ? 0 : 0.5 }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 900, textTransform: "uppercase", letterSpacing: 0.8, color: CATEGORY_COLOR[row.category] ?? "text.secondary" }}>
                      {CATEGORY_LABELS[row.category] ?? row.category}
                    </Typography>
                    <Box sx={{ flex: 1, height: "1px", bgcolor: `${CATEGORY_COLOR[row.category] ?? "#888"}40` }} />
                  </Box>
                )}
                <RubroCard
                  row={row}
                  computed={computed[idx]}
                  calcDate={calcDate}
                  isDark={isDark}
                  showErrors={showValidation}
                  onChange={setRow}
                  onInterests={setRowInterests}
                  onRemove={removeRow}
                  jusValue={jusValue ?? resolvedJusValue}
                />
              </Fragment>
            );
          })}
        </Box>

        {/* Totales — barra simple, estilo municipalidad (sin color por rubro) */}
        {totals && (
          <Box
            sx={{
              ...glassPanel,
              p: 2,
              display: "flex",
              gap: 1.5,
              flexWrap: "wrap",
              alignItems: "stretch",
            }}
          >
            {[
              { key: "capital", label: "Capital", subtotal: Number(totals.capital) || 0, iva: Number(totals.iva_capital) || 0 },
              { key: "gastos", label: "Gastos", subtotal: Number(totals.gastos) || 0, iva: Number(totals.iva_gastos) || 0 },
              { key: "honorarios", label: "Honorarios", subtotal: Number(totals.honorarios) || 0, iva: Number(totals.iva_honorarios) || 0 },
            ].map(({ key, label, subtotal, iva }) => (
              <Box
                key={key}
                sx={{
                  flex: "1 1 120px",
                  minWidth: 110,
                  px: 1.5,
                  py: 1,
                  borderRadius: 1.5,
                  background: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
                  border: isDark ? "1px solid rgba(255,255,255,0.07)" : "1px solid rgba(0,0,0,0.06)",
                }}
              >
                <Typography sx={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, opacity: 0.5, display: "flex", alignItems: "center", gap: 0.4, mb: 0.3 }}>
                  {label}
                  {key === "honorarios" && honorariosHasJusRow && (
                    <Tooltip title="El valor en JUS está redondeado para mostrar. El monto exacto y válido es el que figura en pesos — no multiplicar el JUS redondeado por el valor de JUS para verificar, puede no coincidir.">
                      <InfoOutlinedIcon sx={{ fontSize: 12, opacity: 0.5 }} />
                    </Tooltip>
                  )}
                </Typography>
                <Typography sx={{ fontWeight: 900, fontSize: 13 }}>
                  {key === "honorarios" ? formatHonorarios(subtotal, jusValue ?? resolvedJusValue, honorariosHasJusRow) : formatCurrency(subtotal)}
                </Typography>
                {iva > 0 && (
                  <Box sx={{ mt: 0.6, pt: 0.6, borderTop: isDark ? "1px dashed rgba(255,255,255,0.12)" : "1px dashed rgba(0,0,0,0.12)" }}>
                    <Typography sx={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, opacity: 0.45, display: "block", mb: 0.2 }}>
                      + IVA (21%)
                    </Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: 12, opacity: 0.8 }}>
                      {key === "honorarios" ? formatHonorarios(iva, jusValue ?? resolvedJusValue, honorariosHasJusRow) : formatCurrency(iva)}
                    </Typography>
                  </Box>
                )}
              </Box>
            ))}

            <Box
              sx={{
                ml: "auto",
                px: 2,
                py: 1,
                borderRadius: 2,
                background: isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.04)",
                border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(0,0,0,0.1)",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "flex-end",
                minWidth: 160,
              }}
            >
              <Typography sx={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, opacity: 0.5, mb: 0.3 }}>
                Total liquidación
              </Typography>
              <Typography sx={{ fontWeight: 900, fontSize: 20, color: "primary.main", letterSpacing: -0.5 }}>
                {formatCurrency(totals.grand_total)}
              </Typography>
              <Typography sx={{ fontSize: 10, opacity: 0.55, mt: 0.3 }}>
                IVA total {formatCurrency(Number(totals.iva) || 0)}
              </Typography>
            </Box>
          </Box>
        )}

        {honorariosHasJusRow && (
          <Alert severity="info" sx={{ fontSize: 12.5 }}>
            Los montos de Honorarios en JUS están redondeados a 4 decimales para mostrar. El total
            de la liquidación se calcula siempre sobre los montos exactos en pesos, no sobre el
            valor en JUS redondeado — al multiplicar el JUS mostrado por el valor de JUS puede no
            coincidir exactamente con el total.
          </Alert>
        )}

        <TextField label="Notas (opcional)" value={notes} onChange={(e) => setNotes(e.target.value)} multiline rows={2} size="small" fullWidth />

        {saveError && <Alert severity="error">{saveError}</Alert>}
      </Box>
    </AppDialog>

    <ConfirmDialog
      open={confirmOpen}
      onClose={() => setConfirmOpen(false)}
      onConfirm={doSave}
      loading={saveLoading}
      title={alertWarnings.length > 0 ? "Hay tasas sin publicar" : "Hay advertencias en las tasas"}
      description={
        <>
          <Box component="span" sx={{ display: "block", mb: 1 }}>
            {alertWarnings.length > 0
              ? "Hay tasas de un día hábil que todavía no se publicaron. El interés puede cambiar al recalcular cuando se carguen."
              : "Revisá estas advertencias del cálculo de intereses antes de guardar."}
          </Box>
          <Box component="span" sx={{ display: "block", fontWeight: 600, fontSize: 13, opacity: 0.9 }}>
            {warnings
              .filter((w) => !w.trim().startsWith("ⓘ"))
              .map((w, i) => (
                <Box component="span" key={i} sx={{ display: "block", mb: 0.5 }}>
                  • {w.replace(/^⚠\s*/, "")}
                </Box>
              ))}
          </Box>
          <Box component="span" sx={{ display: "block", mt: 1 }}>
            ¿Guardar la liquidación igual?
          </Box>
        </>
      }
      confirmText="Guardar igual"
      confirmColor={alertWarnings.length > 0 ? "error" : "primary"}
    />

    <ConfirmDialog
      open={!!draftInfo}
      onClose={resumeDraft}
      onConfirm={discardDraft}
      title="Borrador sin guardar"
      description={
        <>
          Hay un borrador de este expediente
          {draftInfo?.savedAt ? ` guardado el ${dayjs(draftInfo.savedAt).format("DD/MM/YYYY HH:mm")}` : ""}
          {draftInfo?.updatedBy ? ` por ${draftInfo.updatedBy}` : ""}
          {draftInfo?.rows?.length ? ` (${draftInfo.rows.length} ${draftInfo.rows.length === 1 ? "rubro" : "rubros"})` : ""}
          . ¿Querés mantenerlo o empezar desde 0?
        </>
      }
      cancelText="Mantener borrador"
      confirmText="Empezar desde 0"
      confirmColor="error"
    />

    <TaxReceiptsDialog
      open={taxOpen}
      onClose={() => setTaxOpen(false)}
      legalFileId={legalFile?.id}
      legalFileCode={legalFile?.code}
      onApplyRubros={(rubros) => {
        applyTaxRubros(rubros);
        setTaxOpen(false);
        showToast("Rubros de Tasa de Justicia, Caja y Colegio cargados desde el comprobante.", "success");
      }}
    />

    <LiquidationSheetDialog
      open={liquidationSheetOpen}
      onClose={() => setLiquidationSheetOpen(false)}
      legalFileId={legalFile?.id}
      legalFileCode={legalFile?.code}
      rows={rows}
      available={available}
      onApply={(actions) => {
        // Un solo setRows atómico: patchea los rubros ya agregados y agrega
        // (con el monto ya cargado) los que matchean un tipo de catálogo
        // nuevo, sin pisarse entre sí si vienen varias acciones juntas.
        setRows((prev) => {
          let next = prev.map((r) => {
            const patch = actions.find((a) => a.kind === "patch" && a.id === r.id);
            return patch ? { ...r, amount_mode: "fixed", fixed_amount: patch.amount } : r;
          });
          const newRows = actions
            .filter((a) => a.kind === "add")
            .map((a) => {
              const row = makeRow(a.rubro, calcDate);
              row.amount_mode = "fixed";
              row.fixed_amount = a.amount;
              row.suggested_fixed_amount = a.amount;
              return row;
            });
          return sortByCategory([...next, ...newRows]);
        });
      }}
    />

    <BancoCordobaRatesDialog open={bancorOpen} onClose={() => setBancorOpen(false)} onChanged={refreshRatePeek} />

    <PreviewDialog
      open={pdfOpen}
      onClose={() => setPdfOpen(false)}
      title="Vista previa — Planilla de Liquidación"
      subtitle="Sin guardar · revisá el cálculo antes de generarlo"
      html={pdfHtml}
      loading={pdfLoading}
    />
    </>
  );
}
